// client/src/pages/csgo/CsgoOverview.js

import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Spinner, Card, Col, Row, Button, Badge, Form, Table } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { FaSync, FaBoxOpen, FaTrophy, FaChartLine, FaDiceFive, FaCrosshairs } from 'react-icons/fa';
import useCsgo from '../../hooks/useCsgo';
import { CHART_PALETTE, CHART_AXIS_LABEL_STYLE, CHART_GRID_COLOR } from '../../utils/chartTheme';

// Tout l'espace CS:GO est en USD (BigQuery et CSFloat sont nativement en dollars).
const usd = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v || 0));
const signed = (v) => `${Number(v) >= 0 ? '+' : ''}${usd(v)}`;

// Les semaines vont du lundi au dimanche : on affiche toujours les deux bornes
// (« 27/07 → 02/08 »), le seul jour de départ prêtant à confusion.
const dayMonth = (d) =>
  new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
const weekRange = (h) => (h ? `${dayMonth(h.weekStart)} → ${dayMonth(h.weekEnd)}` : '');

const MetricCard = ({ title, value, subtitle, tone = 'neutral', icon: Icon }) => {
  const color = tone === 'pos' ? 'text-success' : tone === 'neg' ? 'text-danger' : 'text-accent';
  const border =
    tone === 'pos' ? 'border-success' : tone === 'neg' ? 'border-danger' : 'border-accent';
  return (
    <Col lg={3} sm={6}>
      <Card className={`border-0 shadow-sm h-100 bg-white border-start border-4 ${border}`}>
        <Card.Body className="p-4">
          <div className="small fw-bold text-muted text-uppercase mb-2 d-flex align-items-center">
            {Icon && <Icon className="me-2" size={13} />}
            {title}
          </div>
          <div className={`h3 fw-bold mb-0 ${color}`}>{value}</div>
          {subtitle && <div className="small text-muted mt-2 opacity-75">{subtitle}</div>}
        </Card.Body>
      </Card>
    </Col>
  );
};

// Historique hebdo : barres signées (positif vert / négatif rouge — la polarité est
// d'abord portée par la position vs zéro, la couleur ne fait que renforcer).
const WeeklyHistoryChart = ({ history }) => {
  const chart = useMemo(() => {
    if (!history || !history.some((h) => h.count > 0)) return null;
    return {
      categories: history.map((h) =>
        new Date(h.weekStart).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
      ),
      data: history.map((h) => Number(Number(h.profit).toFixed(2)))
    };
  }, [history]);

  if (!chart) return null;

  return (
    <div className="mt-4 pt-3 border-top">
      <div className="text-xs text-muted text-uppercase fw-bold mb-2">
        Historique (12 dernières semaines)
      </div>
      <Chart
        type="bar"
        height={170}
        series={[{ name: 'Profit snipes', data: chart.data }]}
        options={{
          chart: { toolbar: { show: false }, sparkline: { enabled: false } },
          plotOptions: {
            bar: {
              columnWidth: '55%',
              borderRadius: 3,
              colors: {
                ranges: [
                  { from: -1e9, to: -0.005, color: '#ef4444' },
                  { from: -0.005, to: 1e9, color: '#10b981' }
                ]
              }
            }
          },
          dataLabels: { enabled: false },
          xaxis: {
            categories: chart.categories,
            labels: { style: { ...CHART_AXIS_LABEL_STYLE, fontSize: '10px' } },
            axisBorder: { show: false },
            axisTicks: { show: false }
          },
          yaxis: {
            labels: {
              formatter: (v) => `${Number(v).toFixed(0)} $`,
              style: CHART_AXIS_LABEL_STYLE
            },
            forceNiceScale: true
          },
          grid: { borderColor: CHART_GRID_COLOR },
          tooltip: {
            // Titre du tooltip = plage complète lundi → dimanche (l'axe, lui, reste
            // court pour rester lisible).
            x: {
              formatter: (val, opts) => {
                const h = opts && history[opts.dataPointIndex];
                return h ? `Semaine ${weekRange(h)}` : val;
              }
            },
            y: {
              formatter: (v, { dataPointIndex }) => {
                const h = history[dataPointIndex];
                const paid = h.paid ? ' · réglée ✓' : '';
                return `${signed(v)} · ${h.count} vente(s) · coût ${usd(h.cost)} · dû ${usd(h.due)}${paid}`;
              }
            }
          }
        }}
      />
    </div>
  );
};

// Suivi des règlements au partenaire : une ligne par semaine ayant eu des ventes,
// la plus récente en haut. Cocher = semaine remboursée (le montant dû est figé
// côté serveur au moment du clic).
const WeekPaymentTable = ({ history, onToggle }) => {
  const weeks = useMemo(
    () =>
      (history || [])
        .filter((h) => h.count > 0)
        .slice()
        .reverse(),
    [history]
  );
  const unpaid = weeks.filter((h) => !h.paid);
  const unpaidTotal = unpaid.reduce((s, h) => s + Number(h.due || 0), 0);

  if (!weeks.length) return null;

  return (
    <div className="mt-4 pt-3 border-top">
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <span className="text-xs text-muted text-uppercase fw-bold">Règlements au partenaire</span>
        <span className="small">
          Reste à régler :{' '}
          <strong className={unpaidTotal > 0 ? 'text-danger' : 'text-success'}>
            {usd(unpaidTotal)}
          </strong>
          <span className="text-muted"> · {unpaid.length} semaine(s)</span>
        </span>
      </div>
      <div className="table-responsive">
        <Table borderless size="sm" className="align-middle mb-0">
          <thead className="text-muted text-xs text-uppercase border-bottom">
            <tr>
              <th className="pb-2">Semaine</th>
              <th className="pb-2 text-end">Ventes</th>
              <th className="pb-2 text-end">Dû partenaire</th>
              <th className="pb-2 text-end">Ma part</th>
              <th className="pb-2 text-center">Réglée</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((h) => (
              <tr key={h.weekStart} className={h.paid ? 'opacity-75' : undefined}>
                <td className="py-2 text-sm fw-semibold">{weekRange(h)}</td>
                <td className="py-2 text-end text-sm text-muted">{h.count}</td>
                <td className="py-2 text-end text-sm fw-bold">{usd(h.due)}</td>
                <td
                  className={`py-2 text-end text-sm ${Number(h.profit) >= 0 ? 'text-success' : 'text-danger'}`}
                >
                  {signed(h.profit)}
                </td>
                <td className="py-2 text-center">
                  <Form.Check
                    type="checkbox"
                    className="d-inline-block"
                    checked={!!h.paid}
                    onChange={(e) => onToggle(h, e.target.checked)}
                    aria-label={`Marquer la semaine ${weekRange(h)} comme réglée`}
                    title={
                      h.paid
                        ? 'Semaine réglée — décocher pour la repasser en attente'
                        : 'Marquer cette semaine comme réglée au partenaire'
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

const WeeklyRealizedCard = ({ weekly, history, onToggleWeekPaid }) => {
  if (!weekly) return null;
  const tw = Number(weekly.thisWeek || 0);
  const lw = Number(weekly.lastWeek || 0);
  const delta = tw - lw;
  const Cell = ({ label, value, count, cost, due }) => (
    <Col xs={6} md={4}>
      <div className="text-xs text-muted text-uppercase fw-bold mb-1">{label}</div>
      <div className={`h4 fw-bold mb-0 ${value >= 0 ? 'text-success' : 'text-danger'}`}>
        {signed(value)}
      </div>
      {count != null && (
        <div className="small text-muted">
          {count} vente(s) · coût {usd(cost)} ·{' '}
          <span title="Remboursement des achats + 50 % du profit brut à reverser au partenaire">
            dû partenaire {usd(due)}
          </span>
        </div>
      )}
    </Col>
  );
  return (
    <Col lg={12}>
      <Card className="border-0 shadow-sm bg-white border-start border-4 border-success">
        <Card.Body className="p-4">
          <Card.Title className="h6 fw-bold mb-3 d-flex align-items-center">
            <FaTrophy className="me-2 text-success" /> Ma part snipes — semaine (lun. → dim.)
          </Card.Title>
          <Row className="g-3">
            <Cell
              label="Cette semaine"
              value={tw}
              count={weekly.thisWeekCount}
              cost={weekly.thisWeekCost}
              due={weekly.thisWeekDue}
            />
            <Cell
              label="Semaine dernière"
              value={lw}
              count={weekly.lastWeekCount}
              cost={weekly.lastWeekCost}
              due={weekly.lastWeekDue}
            />
            <Col xs={12} md={4}>
              <div className="text-xs text-muted text-uppercase fw-bold mb-1">Évolution</div>
              <div className={`h4 fw-bold mb-0 ${delta >= 0 ? 'text-success' : 'text-danger'}`}>
                {delta >= 0 ? '▲ ' : '▼ '}
                {signed(delta)}
              </div>
              <div className="small text-muted">vs semaine dernière</div>
            </Col>
          </Row>
          <WeeklyHistoryChart history={history} />
          <WeekPaymentTable history={history} onToggle={onToggleWeekPaid} />
        </Card.Body>
      </Card>
    </Col>
  );
};

const StatusBreakdown = ({ counts }) => (
  <Col lg={6}>
    <Card className="border-0 shadow-sm bg-white h-100">
      <Card.Body className="p-4">
        <Card.Title className="h6 fw-bold mb-3 d-flex align-items-center">
          <FaCrosshairs className="me-2 text-primary" /> Items suivis
        </Card.Title>
        <div className="d-flex flex-wrap gap-2">
          <Badge bg="primary" className="px-3 py-2">
            En inventaire : {counts.inInventory}
          </Badge>
          <Badge bg="success" className="px-3 py-2">
            Vendus : {counts.sold}
          </Badge>
          {counts.gone > 0 && (
            <Badge bg="secondary" className="px-3 py-2">
              Sortis (hors CSFloat) : {counts.gone}
            </Badge>
          )}
          <Badge bg="info" className="px-3 py-2">
            Snipes détenus : {counts.snipesHeld}
          </Badge>
          <Badge bg="dark" className="px-3 py-2">
            Snipes au total : {counts.snipesTotal}
          </Badge>
        </div>
      </Card.Body>
    </Card>
  </Col>
);

const GamblingCard = ({ gamblingStats }) => {
  if (!gamblingStats) return null;
  const net = Number(gamblingStats.netProfit || 0);
  const roi = Number(gamblingStats.roi || 0);
  return (
    <Col lg={6}>
      <Card className="border-0 shadow-sm bg-white h-100 border-start border-4 border-accent">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Card.Title className="h6 fw-bold mb-0 d-flex align-items-center">
              <FaDiceFive className="me-2 text-accent" /> Gambling
            </Card.Title>
            <Badge bg={roi >= 0 ? 'success' : 'danger'}>
              {roi >= 0 ? '+' : ''}
              {roi.toFixed(1)}%
            </Badge>
          </div>
          <div className={`h4 fw-bold mb-2 ${net >= 0 ? 'text-success' : 'text-danger'}`}>
            {signed(net)}
          </div>
          <Row className="g-2 pt-2 border-top">
            <Col xs={6}>
              <div className="text-xs text-muted text-uppercase fw-bold mb-1">Dépôts</div>
              <div className="fw-bold text-sm">{usd(gamblingStats.totalDeposits)}</div>
            </Col>
            <Col xs={6}>
              <div className="text-xs text-muted text-uppercase fw-bold mb-1">Gains</div>
              <div className="fw-bold text-sm text-success">{usd(gamblingStats.totalWins)}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Col>
  );
};

const EvolutionChart = ({ portfolioHistory }) => {
  const series = useMemo(
    () => [
      { name: 'Valeur détenue', data: portfolioHistory.map((p) => Number(p.totalValue).toFixed(2)) }
    ],
    [portfolioHistory]
  );
  const categories = useMemo(() => portfolioHistory.map((p) => p.date), [portfolioHistory]);

  if (!portfolioHistory.length) {
    return (
      <Col lg={12}>
        <Card className="border-0 shadow-sm bg-white">
          <Card.Body className="p-4 text-center text-muted">
            <FaChartLine className="mb-2" size={22} />
            <div>La courbe d'évolution se construit à partir du snapshot quotidien.</div>
            <div className="small">Elle se remplira au fil des jours (cron quotidien).</div>
          </Card.Body>
        </Card>
      </Col>
    );
  }

  return (
    <Col lg={12}>
      <Card className="border-0 shadow-sm bg-white">
        <Card.Body className="p-4">
          <Card.Title className="h6 fw-bold mb-4 d-flex align-items-center">
            <FaChartLine className="me-2 text-primary" /> Évolution de la valeur détenue
          </Card.Title>
          <Chart
            options={{
              chart: { id: 'csgo-evolution', toolbar: { show: false } },
              xaxis: { type: 'datetime', categories, labels: { style: CHART_AXIS_LABEL_STYLE } },
              yaxis: {
                labels: {
                  formatter: (v) => `${Number(v).toFixed(0)} $`,
                  style: CHART_AXIS_LABEL_STYLE
                }
              },
              stroke: { curve: 'smooth', width: 3 },
              colors: [CHART_PALETTE[0]],
              fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
              dataLabels: { enabled: false },
              grid: { borderColor: CHART_GRID_COLOR }
            }}
            series={series}
            type="area"
            height={300}
          />
        </Card.Body>
      </Card>
    </Col>
  );
};

function CsgoOverview() {
  const { overview, portfolioHistory, loading, error, fetchOverview, sync, setWeekPaid } =
    useCsgo();
  const [syncMsg, setSyncMsg] = useState(null);

  // Le montant dû est figé côté serveur : on envoie celui affiché au moment du clic.
  const handleToggleWeekPaid = async (week, paid) => {
    try {
      await setWeekPaid(week.weekStart, paid, paid ? Number(week.due || 0) : null);
    } catch {
      // erreur déjà remontée par le hook (et la case revient à son état d'avant)
    }
  };

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handleSync = async () => {
    setSyncMsg(null);
    try {
      const res = await sync();
      setSyncMsg(
        `Sync OK : ${res.created} snipe(s) créé(s), ${res.updated} mis à jour, ${res.tagged} item(s) taggué(s) snipe.`
      );
      await fetchOverview();
    } catch {
      // erreur déjà remontée par le hook
    }
  };

  if (loading.overview && !overview) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted fw-bold">Chargement...</p>
      </div>
    );
  }

  const counts = overview?.counts || {
    inInventory: 0,
    sold: 0,
    gone: 0,
    snipesHeld: 0,
    snipesTotal: 0
  };

  return (
    <div className="animate-fade-in pb-5">
      <div className="d-flex justify-content-between align-items-center mb-4 gap-3 flex-wrap">
        <h1 className="h4 mb-0 fw-bold">Vue d'ensemble CS:GO</h1>
        <Button variant="primary" size="sm" onClick={handleSync} disabled={loading.sync}>
          <FaSync className={`me-2 ${loading.sync ? 'fa-spin' : ''}`} />
          {loading.sync ? 'Synchronisation...' : 'Sync BigQuery + tag snipes'}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {syncMsg && (
        <Alert variant="success" dismissible onClose={() => setSyncMsg(null)}>
          {syncMsg}
        </Alert>
      )}

      <Row className="g-4 mb-4">
        <MetricCard
          title="Valeur détenue"
          value={usd(overview?.heldValueUsd)}
          subtitle={`${counts.inInventory} item(s) en inventaire`}
          icon={FaBoxOpen}
        />
        <MetricCard
          title="Ma part réalisée"
          value={signed(overview?.realizedProfitUsd)}
          subtitle="Après remboursements + 50 % au partenaire, net des frais de vente"
          tone={Number(overview?.realizedProfitUsd) >= 0 ? 'pos' : 'neg'}
          icon={FaTrophy}
        />
        <MetricCard
          title="Dû au partenaire"
          value={usd(overview?.realizedPartnerDueUsd)}
          subtitle="Remboursement des achats + 50 % du profit brut (ventes réalisées)"
        />
        <MetricCard
          title="Ma part latente"
          value={signed(overview?.latentProfitUsd)}
          subtitle="Estimation sur l'inventaire, partage partenaire déduit"
          tone={Number(overview?.latentProfitUsd) >= 0 ? 'pos' : 'neg'}
        />
      </Row>

      <Row className="g-4 mb-4">
        <WeeklyRealizedCard
          weekly={overview?.weeklyRealized}
          history={overview?.weeklyHistory}
          onToggleWeekPaid={handleToggleWeekPaid}
        />
      </Row>

      <Row className="g-4 mb-4">
        <StatusBreakdown counts={counts} />
        <GamblingCard gamblingStats={overview?.gambling} />
      </Row>

      <Row className="g-4">
        <EvolutionChart portfolioHistory={portfolioHistory} />
      </Row>
    </div>
  );
}

export default CsgoOverview;
